/* eslint-disable react-refresh/only-export-components -- co-located helper; HMR-only warning */
// SAB-401: NFL mobile players sort menu. Mirrors
// mlb-beta/players-mobile/SortMenu.tsx with NFL sort keys matching the
// RPC's accepted p_sort_key values (see get_nfl_player_leaderboard).
import { ArrowUpDown, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// Mirrors the SortKey union in NFLPlayersPage.tsx so the mobile + desktop
// pages can swap freely without changing what the RPC accepts.
export type MobileSortKey =
  // SAB-811: the shipped board first -- these are what a draft is run off.
  | 'proj_sab_pts'
  | 'adp'
  | 'total_sabotage'
  | 'avg_sabotage'
  | 'total_trash'
  | 'total_anti'
  | 'games_played'
  // SAB-1176: the six stat keys. Sorted SERVER-SIDE (migration 20260914200000) -- the list
  // is paged, so a client-side sort would order only the current page.
  | 'total_yds'
  | 'total_td'
  | 'total_int'
  | 'total_fum'
  | 'total_drp'
  | 'total_tfl'
  | 'full_name';
export type SortDir = 'asc' | 'desc';

const SORT_LABELS: Record<MobileSortKey, string> = {
  proj_sab_pts:   'Projected points',
  adp:            'ADP (best first)',
  total_sabotage: 'Sabotage points',
  avg_sabotage:   'Avg sabotage / G',
  total_trash:    'Trash points',
  total_anti:     'Anti points',
  games_played:   'Games played',
  total_yds:      'Yards allowed',
  total_td:       'Touchdowns allowed',
  total_int:      'Interceptions',
  total_fum:      'Fumbles',
  total_drp:      'Drops',
  total_tfl:      'Tackles for loss',
  full_name:      'Name (A-Z)',
};

// Short trigger labels keep the bar compact under the search row.
const TRIGGER_LABELS: Record<MobileSortKey, string> = {
  total_yds:      'YDS',
  total_td:       'TD',
  total_int:      'INT',
  total_fum:      'FUM',
  total_drp:      'DRP',
  total_tfl:      'TFL',
  proj_sab_pts:   'Proj',
  adp:            'ADP',
  total_sabotage: 'Sab',
  avg_sabotage:   'Sab/G',
  total_trash:    'Trash',
  total_anti:     'Anti',
  games_played:   'GP',
  full_name:      'Name',
};

export function getSortLabel(key: MobileSortKey): string {
  return SORT_LABELS[key] ?? key;
}

export function getSortTriggerLabel(key: MobileSortKey): string {
  return TRIGGER_LABELS[key] ?? key;
}

interface SortMenuProps {
  sortKey: MobileSortKey;
  sortDir: SortDir;
  onSortKeyChange: (key: MobileSortKey) => void;
  onSortDirChange: (dir: SortDir) => void;
}

export function SortMenu({
  sortKey, sortDir, onSortKeyChange, onSortDirChange,
}: SortMenuProps) {
  const items: { key: MobileSortKey; label: string }[] = [
    // SAB-811: board-first ordering. Drafting is the job this menu is used for.
    { key: 'proj_sab_pts',   label: SORT_LABELS.proj_sab_pts },
    { key: 'adp',            label: SORT_LABELS.adp },
    { key: 'total_sabotage', label: SORT_LABELS.total_sabotage },
    { key: 'avg_sabotage',   label: SORT_LABELS.avg_sabotage },
    { key: 'total_trash',    label: SORT_LABELS.total_trash },
    { key: 'total_anti',     label: SORT_LABELS.total_anti },
    { key: 'games_played',   label: SORT_LABELS.games_played },
    { key: 'full_name',      label: SORT_LABELS.full_name },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[11px] font-display font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          aria-label="Sort"
        >
          <ArrowUpDown className="w-3 h-3" />
          <span>Sort: {getSortTriggerLabel(sortKey)}</span>
          <span aria-hidden="true">{sortDir === 'desc' ? '↓' : '↑'}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        {items.map((it) => (
          <DropdownMenuItem
            key={it.key}
            onClick={() => onSortKeyChange(it.key)}
            className="flex items-center justify-between"
          >
            <span className={cn(it.key === sortKey && 'text-primary font-semibold')}>
              {it.label}
            </span>
            {it.key === sortKey && <Check className="w-3.5 h-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onSortDirChange(sortDir === 'desc' ? 'asc' : 'desc')}
          className="text-xs text-muted-foreground"
        >
          Direction: {sortDir === 'desc' ? 'High → Low' : 'Low → High'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default SortMenu;
